import { useContext, type ReactNode } from 'react'
import { useMediaDeviceListener } from 'hooks/av-test-listener'
import { MediaDevicePreviewContext } from 'hooks/use-media-device-preview'

function SelfHostedMediaDevicePreviewProvider({
  children,
}: {
  children: ReactNode
}) {
  const value = useMediaDeviceListener()
  return (
    <MediaDevicePreviewContext.Provider value={value}>
      {children}
    </MediaDevicePreviewContext.Provider>
  )
}

/**
 * Hoists a single `usePreviewTracks` instance to all descendants so we don't
 * trigger duplicate `getUserMedia` captures.
 *
 * Idempotent: if a parent already provides a value, this renders as a
 * pass-through. This lets `NewAvTest` always wrap itself in a provider so
 * its standalone consumers work, while `AvCheck` provides a higher-level
 * instance that takes precedence when both are in the tree.
 */
export function MediaDevicePreviewContextProvider({
  children,
}: {
  children: ReactNode
}) {
  const parent = useContext(MediaDevicePreviewContext)
  if (parent) return <>{children}</>
  return (
    <SelfHostedMediaDevicePreviewProvider>
      {children}
    </SelfHostedMediaDevicePreviewProvider>
  )
}
